import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  InfoCircleOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Flex } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import Tooltip from '@totem/components/common/tooltip/Tooltip';
import DeviceActionMenu from '@totem/components/devices/DeviceActionMenu';
import DeviceContext from '@totem/components/devices/devicesContainer/deviceContainerContext';
import { GatewayStatus } from '@totem/components/services/remoteAccess/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import colors from '@totem/styles/colors';
import { Device, DeviceQueryResult } from '@totem/types/devices';
import { isNotNull } from '@totem/utilities/common';
import { DEVICES_ENDPOINT } from '@totem/utilities/endpoints';

import '../../../../devices/devices.css';

const styles = {
  infoIcon: {
    color: colors.neutral.gray,
    cursor: 'pointer',
  },
  tooltipList: {
    margin: '0.25rem 0 0.75rem',
    paddingLeft: '1.1rem',
  },
  tooltipListLast: {
    margin: '0.25rem 0 0',
    paddingLeft: '1.1rem',
  },
};

const ACTIVE_CONNECTED_TOOLTIP = (
  <div>
    <div>✅ Active</div>
    <ul style={styles.tooltipList}>
      <li>
        This means the device or gateway has been provisioned and authorized in
        the Airwall system.
      </li>
      <li>It is allowed to participate in the overlay network.</li>
      <li>
        However, it might not currently have an online connection to the Airwall
        Conductor.
      </li>
      <li>
        Think of it as: &ldquo;This device is approved and ready to go.&rdquo;
      </li>
    </ul>
    <div>🌐 Connected</div>
    <ul style={styles.tooltipListLast}>
      <li>
        This means the device or gateway is currently online and has an active
        session with the Airwall Conductor.
      </li>
      <li>
        It&rsquo;s communicating in real time&mdash;either sending or receiving
        traffic over the overlay.
      </li>
      <li>Think of it as: &ldquo;This device is online and talking.&rdquo;</li>
    </ul>
  </div>
);

const GatewayDeviceTable = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const [refCount, setRefCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [airwalIds, setAirwalIds] = useState<string[]>([]);
  const [key, setKey] = useState(0);
  const [gatewayStatuses, setGatewayStatuses] = useState<GatewayStatus[]>([]);
  const { loading, deviceData, totalRecords } = useContext(DeviceContext);
  const { input, handleTableChange, getFilterValues, getFilterOptions } =
    useContext(InputContext);

  useEffect(() => {
    if (isNotNull(deviceData)) {
      const deviceAirwalIds = deviceData.devices.map((chkDevice) => {
        const airwalKeys = chkDevice.device.keys.filter(
          (itemKey) => itemKey.type === 'TEMPERED_ID',
        );
        if (typeof airwalKeys !== 'undefined') {
          return airwalKeys[0].value;
        }
        return null;
      });
      setAirwalIds(deviceAirwalIds.filter((airwalId) => airwalId !== null));
    }
  }, [deviceData]);

  useEffect(() => {
    if (isNotNull(airwalIds) && airwalIds.length > 0) {
      const endpoint = `${DEVICES_ENDPOINT}/report/gateways/statuses`;
      setIsSending(true);

      fetch(`${endpoint}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(airwalIds),
      })
        .then((res) => res.json())
        .then((result: GatewayStatus[]) => {
          if (isNotNull(result) && result.length > 0) {
            setGatewayStatuses(result);

            setKey(key + 1);
          }
        })
        .then(() => {
          setIsSending(false);
        });
    }
  }, [airwalIds, refCount, getApiToken]);

  const getBuildingDisplay = (device: DeviceQueryResult) => {
    if (
      device.building !== null &&
      device.building.id !== null &&
      device.building.id !== EMPTY_ID
    ) {
      return (
        <Link to={`/dashboard/buildings/${device.building.id}`}>
          {device.building.name}
        </Link>
      );
    } else if (device.building !== null) {
      return <span>{device.building.name}</span>;
    }
    return <span>-</span>;
  };

  const getStatus = (device: DeviceQueryResult) => {
    if (isNotNull(gatewayStatuses)) {
      const airwalKeys = device.device.keys.filter(
        (itemKey) => itemKey.type === 'TEMPERED_ID',
      );
      if (typeof airwalKeys !== 'undefined') {
        const status = gatewayStatuses.find(
          (chk) => chk.airwallId === airwalKeys[0].value,
        );
        if (typeof status !== 'undefined') {
          return (
            <span>
              {status.active ? (
                <span style={{ color: colors.utility.positive }}>True</span>
              ) : (
                <span style={{ color: colors.utility.negative }}>False</span>
              )}{' '}
              /{' '}
              {status.connected ? (
                <span style={{ color: colors.utility.positive }}>True</span>
              ) : (
                <span style={{ color: colors.utility.negative }}>False</span>
              )}
            </span>
          );
        }
      }
    }
    return <span>- / -</span>;
  };

  const getContractLine = (device: Device) => {
    if (device.salesOrders !== null && device.salesOrders.length > 0) {
      const activeLines = device.salesOrders.filter(
        (cl) => cl.status === 'Active',
      );
      return (
        <span>
          {activeLines.map((cl, idx) => {
            if (cl.service === 'Remote Access') {
              return (
                <Link
                  key={cl.service + cl.salesOrderId + cl.lineNumber}
                  to={`/dashboard/salesOrder/${encodeURIComponent(cl.salesOrderNumber)}`}
                  title={cl.service}
                >
                  {idx > 0 && <br />}
                  {cl.salesOrderNumber}:{cl.lineNumber}
                </Link>
              );
            }
            return (
              <span
                key={cl.service + cl.salesOrderId + cl.lineNumber}
                style={{ color: '#D39600' }}
                title={cl.service}
              >
                {idx > 0 && <br />}
                {cl.salesOrderNumber}:{cl.lineNumber}
              </span>
            );
          })}
        </span>
      );
    }
    return <span>-</span>;
  };

  const getAttributeValue = (
    device: DeviceQueryResult,
    attributeName: string,
  ) => {
    if (
      device.device !== null &&
      device.device.attributes !== null &&
      device.device.attributes.length > 0
    ) {
      for (let idx = 0; idx < device.device.attributes.length; idx++) {
        if (device.device.attributes[idx].name === attributeName) {
          return <span>{device.device.attributes[idx].value}</span>;
        }
      }
    }
    return <span>-</span>;
  };

  const columns: ColumnProps<DeviceQueryResult>[] = [
    {
      title: 'Name',
      dataIndex: 'device.displayName',
      key: 'displayName',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => (
        <Link to={`/dashboard/devices/${device.device.id}`}>
          {device.device.displayName}
        </Link>
      ),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.device.displayName === compB.device.displayName
          ? 0
          : compA.device.displayName > compB.device.displayName
            ? 1
            : -1,
    },
    {
      title: 'Host Name',
      dataIndex: 'device.attributes.host',
      key: 'host',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Hostname'),
    },
    {
      title: 'Model',
      dataIndex: 'device.attributes.model',
      key: 'model',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Model'),
      filterMultiple: true,
      filteredValue: getFilterValues('model'),
      filters: getFilterOptions('model'),
    },
    {
      title: 'Version',
      dataIndex: 'device.attributes.version',
      key: 'version',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Version'),
    },
    {
      title: 'Serial Number',
      dataIndex: 'device.attributes.serialNumber',
      key: 'serialNumber',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Serial Number'),
    },
    {
      title: 'Asset Tag',
      dataIndex: 'device.attributes.assetTag',
      key: 'assetTag',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Asset Tag'),
    },
    {
      title: 'Firmware Revision',
      dataIndex: 'device.attributes.firmware',
      key: 'firmware',
      render: (_, device: DeviceQueryResult) =>
        getAttributeValue(device, 'Firmware Revision'),
    },
    {
      title: 'Sales Order Line',
      dataIndex: 'device.salesOrder.salesOrderNumber',
      key: 'salesOrderNumber',
      render: (_, device: DeviceQueryResult) => {
        return getContractLine(device.device);
      },
    },
    {
      title: (
        <Flex align={'center'} gap={4} wrap={'nowrap'}>
          <span>Active / Connected</span>
          <Tooltip title={ACTIVE_CONNECTED_TOOLTIP}>
            <InfoCircleOutlined style={styles.infoIcon} />
          </Tooltip>
          {isSending ? (
            <LoadingOutlined />
          ) : (
            <ReloadOutlined
              title={'Refresh Gateway Status'}
              onClick={() => setRefCount(refCount + 1)}
            />
          )}
        </Flex>
      ),
      key: 'device.id',
      render: (_, device: DeviceQueryResult) => {
        return getStatus(device);
      },
    },
    {
      title: 'Building',
      dataIndex: 'building.name',
      key: 'building',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => getBuildingDisplay(device),
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.building.name === compB.building.name
          ? 0
          : compA.building.name > compB.building.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('building'),
      filters: getFilterOptions('building'),
    },
    {
      title: 'Region',
      dataIndex: 'region.name',
      key: 'region',
      showSorterTooltip: true,
      render: (_, device: DeviceQueryResult) => device.region.name,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.region.name === compB.region.name
          ? 0
          : compA.region.name > compB.region.name
            ? 1
            : -1,
      filterMultiple: true,
      filteredValue: getFilterValues('region'),
      filters: getFilterOptions('region'),
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'additionalOptions',
      render: (_, device: DeviceQueryResult) => (
        <div className="center-table-cell">
          <DeviceActionMenu device={device} />
        </div>
      ),
    },
  ];

  const getDevices = () => {
    if (deviceData !== null) {
      if (deviceData.devices !== null) {
        return deviceData.devices;
      }
    }
    return [];
  };

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={getDevices()}
      loading={loading}
      onChange={(updatedPagination, filters, sorter, extra) =>
        handleTableChange(updatedPagination, filters, sorter, extra)
      }
      pagination={{
        current: input.pagination.page,
        pageSize: input.pagination.pageSize,
        total: totalRecords,
        showSizeChanger: true,
      }}
      key={key}
      rowKey={(record) => record.device.id}
    />
  );
};

export default GatewayDeviceTable;
